import React, { useState } from 'react';
import {
  Switch,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Badges from './pages/Components/Badges';
import Buttons from './pages/Components/Buttons';
import ColorSelectDocs from './pages/Components/ColorSelectDocs';
import CreatableSelectDocs from './pages/Components/CreatableSelect';
import DataTableDocs from './pages/Components/DataTable';
import DateSelectDocs from './pages/Components/DateSelectDocs';
import DateTimePickerDocs from './pages/Components/DateTimePicker';
import FileUpload from './pages/Components/FileUpload';
import Forms from './pages/Components/Forms';
import ModalDocs from './pages/Components/Modal';
import MultiSelectDocs from './pages/Components/MultiSelectDocs';
import QuickActionsDocs from './pages/Components/QuickActions';
import RadioDocs from './pages/Components/Radio';
import RelativeDatePickerDocs from './pages/Components/RelativeDatePickerDocs';
import SearchableSelectDocs from './pages/Components/SearchableSelectDocs';
import SelectDocs from './pages/Components/Select';
import TableFilterDocs from './pages/Components/TableFilter';
import TabsDocs from './pages/Components/Tabs';
import ToasterDocs from './pages/Components/Toaster';
import Colors from './pages/DesignTokens/Colors';
import Shadows from './pages/DesignTokens/Shadows';
import Spacing from './pages/DesignTokens/Spacing';
import Typography from './pages/DesignTokens/Typography';
import CardsPage from './pages/Foundation/Cards';
import Layout from './pages/Foundation/Layout';
import Overview from './pages/Overview';
import MetricsDocs from './pages/Patterns/Metrics';
import RoutingDocs from './pages/Patterns/Routing';
import TablesDocs from './pages/Patterns/Tables';
import TimelineDocs from './pages/Patterns/Timeline';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : '#f8fafc'};
`;

const Sidebar = styled.aside`
  width: 280px;
  flex-shrink: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
  border-right: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  padding: 32px 0;
  overflow-y: auto;
  position: sticky;
  top: 0;
  height: 100vh;

  @media (max-width: 968px) {
    position: fixed;
    left: ${(props) => (props.isOpen ? '0' : '-280px')};
    z-index: 1000;
    transition: left 0.3s ease;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 48px;
  max-width: 1400px;
  margin: 0 auto;
  min-width: 0;
  width: 100%;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const Logo = styled.div`
  padding: 0 24px 24px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  margin-bottom: 24px;
`;

const LogoText = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogoSubtext = styled.p`
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
`;

const NavSection = styled.div`
  padding: 0 16px;
  margin-bottom: 24px;
`;

const NavSectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 8px 8px;
  margin: 0 0 8px 0;
`;

const NavLink = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: ${(props) =>
    props.active
      ? props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.08)'
      : 'transparent'};
  border: none;
  border-left: 3px solid
    ${(props) => (props.active ? '#FA4616' : 'transparent')};
  border-radius: 6px;
  color: ${(props) =>
    props.active
      ? '#FA4616'
      : props.theme.name === 'dark'
      ? '#E2E8F0'
      : '#334155'};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;

  &:hover {
    background: ${(props) =>
      props.active
        ? props.theme.name === 'dark'
          ? 'rgba(250, 70, 22, 0.15)'
          : 'rgba(250, 70, 22, 0.08)'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.02)'};
    color: ${(props) => (props.active ? '#FA4616' : '#FA4616')};
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: ${(props) => (props.active ? '1' : '0.7')};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fa4616;
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(250, 70, 22, 0.4);
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 968px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileOverlay = styled.div`
  display: none;

  @media (max-width: 968px) {
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const StyleGuide = () => {
  const location = useLocation();
  const history = useHistory();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine route prefix
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const baseRoute = `${routePrefix}/style-guide`;

  const navigate = (path) => {
    history.push(`${baseRoute}${path}`);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '') {
      return (
        location.pathname === baseRoute || location.pathname === `${baseRoute}/`
      );
    }
    return location.pathname.startsWith(`${baseRoute}${path}`);
  };

  const navigation = [
    {
      title: 'Getting Started',
      items: [{ label: 'Overview', path: '', icon: '📖' }],
    },
    {
      title: 'Design Tokens',
      items: [
        { label: 'Colors', path: '/tokens/colors', icon: '🎨' },
        { label: 'Typography', path: '/tokens/typography', icon: '✍️' },
        { label: 'Spacing', path: '/tokens/spacing', icon: '📏' },
        { label: 'Shadows', path: '/tokens/shadows', icon: '☁️' },
      ],
    },
    {
      title: 'Foundation',
      items: [
        { label: 'Cards', path: '/foundation/cards', icon: '🃏' },
        { label: 'Layout', path: '/foundation/layout', icon: '📐' },
      ],
    },
    {
      title: 'Components',
      items: [
        { label: 'Buttons', path: '/components/buttons', icon: '🔘' },
        { label: 'Forms', path: '/components/forms', icon: '📝' },
        { label: 'Select', path: '/components/select', icon: '📋' },
        { label: 'Radio', path: '/components/radio', icon: '🔘' },
        {
          label: 'Searchable Select',
          path: '/components/searchableselect',
          icon: '🔎',
        },
        {
          label: 'Creatable Select',
          path: '/components/creatableselect',
          icon: '🔽',
        },
        {
          label: 'MultiSelect',
          path: '/components/multiselect',
          icon: '☑️',
        },
        {
          label: 'Color Select',
          path: '/components/colorselect',
          icon: '🎨',
        },
        { label: 'DataTable', path: '/components/datatable', icon: '📋' },
        {
          label: 'Date Select',
          path: '/components/dateselect',
          icon: '📅',
        },
        {
          label: 'Date & Time Picker',
          path: '/components/datetimepicker',
          icon: '🕐',
        },
        {
          label: 'Relative Date',
          path: '/components/relativedatepicker',
          icon: '⏱️',
        },
        { label: 'File Upload', path: '/components/fileupload', icon: '📎' },
        { label: 'Badges', path: '/components/badges', icon: '🏷️' },
        { label: 'Modal', path: '/components/modal', icon: '🪟' },
        { label: 'Tabs', path: '/components/tabs', icon: '📑' },
        { label: 'Toaster', path: '/components/toaster', icon: '🔔' },
        {
          label: 'Quick Actions',
          path: '/components/quickactions',
          icon: '⚡',
        },
        {
          label: 'Table Filter',
          path: '/components/tablefilter',
          icon: '🔍',
        },
      ],
    },
    {
      title: 'Patterns',
      items: [
        { label: 'Metrics', path: '/patterns/metrics', icon: '📊' },
        { label: 'Timeline', path: '/patterns/timeline', icon: '⏳' },
        { label: 'Tables', path: '/patterns/tables', icon: '📋' },
        { label: 'Context Routing', path: '/patterns/routing', icon: '🗺️' },
      ],
    },
  ];

  return (
    <EnhancedThemeProvider>
      <Container>
        <MobileOverlay
          isOpen={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(false)}
        />

        <Sidebar isOpen={mobileMenuOpen}>
          <Logo>
            <LogoText>
              {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
              <span>🎨</span> Style Guide
            </LogoText>
            <LogoSubtext>PitPay Admin Design System</LogoSubtext>
          </Logo>

          {navigation.map((section) => (
            <NavSection key={section.title}>
              <NavSectionTitle>{section.title}</NavSectionTitle>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  active={isActive(item.path)}
                  onClick={() => navigate(item.path)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </NavSection>
          ))}
        </Sidebar>

        <MainContent>
          <Switch>
            <Route exact path={`${baseRoute}`} component={Overview} />
            <Route exact path={`${baseRoute}/`} component={Overview} />

            {/* Design Tokens */}
            <Route
              exact
              path={`${baseRoute}/tokens/colors`}
              component={Colors}
            />
            <Route
              exact
              path={`${baseRoute}/tokens/typography`}
              component={Typography}
            />
            <Route
              exact
              path={`${baseRoute}/tokens/spacing`}
              component={Spacing}
            />
            <Route
              exact
              path={`${baseRoute}/tokens/shadows`}
              component={Shadows}
            />

            {/* Foundation */}
            <Route
              exact
              path={`${baseRoute}/foundation/cards`}
              component={CardsPage}
            />
            <Route
              exact
              path={`${baseRoute}/foundation/layout`}
              component={Layout}
            />

            {/* Components */}
            <Route
              exact
              path={`${baseRoute}/components/buttons`}
              component={Buttons}
            />
            <Route
              exact
              path={`${baseRoute}/components/forms`}
              component={Forms}
            />
            <Route
              exact
              path={`${baseRoute}/components/select`}
              component={SelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/radio`}
              component={RadioDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/creatableselect`}
              component={CreatableSelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/searchableselect`}
              component={SearchableSelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/multiselect`}
              component={MultiSelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/colorselect`}
              component={ColorSelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/dateselect`}
              component={DateSelectDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/relativedatepicker`}
              component={RelativeDatePickerDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/datatable`}
              component={DataTableDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/datetimepicker`}
              component={DateTimePickerDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/fileupload`}
              component={FileUpload}
            />
            <Route
              exact
              path={`${baseRoute}/components/badges`}
              component={Badges}
            />
            <Route
              exact
              path={`${baseRoute}/components/modal`}
              component={ModalDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/tabs`}
              component={TabsDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/toaster`}
              component={ToasterDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/quickactions`}
              component={QuickActionsDocs}
            />
            <Route
              exact
              path={`${baseRoute}/components/tablefilter`}
              component={TableFilterDocs}
            />

            {/* Patterns */}
            <Route
              exact
              path={`${baseRoute}/patterns/metrics`}
              component={MetricsDocs}
            />
            <Route
              exact
              path={`${baseRoute}/patterns/timeline`}
              component={TimelineDocs}
            />
            <Route
              exact
              path={`${baseRoute}/patterns/tables`}
              component={TablesDocs}
            />
            <Route
              exact
              path={`${baseRoute}/patterns/routing`}
              component={RoutingDocs}
            />

            {/* Redirect unknown routes */}
            <Redirect to={`${baseRoute}`} />
          </Switch>
        </MainContent>

        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </MobileMenuButton>
      </Container>
    </EnhancedThemeProvider>
  );
};

export default StyleGuide;
